import React, { useState, useMemo, Fragment } from "react";
import { useSelector } from 'react-redux';
import PropTypes from "prop-types";
import { Box, Grid } from "@mui/material";
import { Icons } from "@gpstrackit/gtc-react-controls";
import { Checkbox, Loading } from "Components";
import NavigationIcon from '@mui/icons-material/Navigation';
import Popover from '@mui/material/Popover';

// Styles
import "./styles.scss";
import { useIntl } from "react-intl";
import { getTrailsLegends } from "../utils";

const FilterEventsMap = ({
  items = [],
  onChange = null,
  value = [],
  buttonLabel = "Event Filters",
  buttonIcon = null,
} = {}) => {
  const { messages } = useIntl();
  const allTrailsLegends = getTrailsLegends();

  const vehicleTrails = useSelector(
    (state) => state.realtimeMapUtilitiesvehiclesTrailRedux.vehicleTrails
  );
  const loadVehicleTrails = useSelector(
    (state) => state.realtimeMapUtilitiesvehiclesTrailRedux.loadVehicleTrails
  );

  // Get unique event names from vehicleTrails
  const eventNamesInTrails = useMemo(() => {
    if (!vehicleTrails || vehicleTrails?.items?.length === 0) return new Set();
    return new Set(vehicleTrails.items.map(trail => trail.eventName).filter(Boolean));
  }, [vehicleTrails?.items]);

  // Filter legends to only show those that have events in vehicleTrails
  const trailsLegends = useMemo(() => {
    if (eventNamesInTrails.size === 0) return allTrailsLegends;
    return allTrailsLegends.filter(legend => {
      // If filterable is false or undefined, always include
      if (legend.filterable === false || legend.filterable === undefined) {
        return true;
      }
      // If filterable is true, only include if it has matching events
      return eventNamesInTrails.has(legend.id);
    });
  }, [allTrailsLegends, eventNamesInTrails]);

  const [isFiltersOpened, setIsFiltersOpened] = useState(false)

  const [selectedItems, setSelectedItems] = useState(trailsLegends.map((item) => item.id));
  const handleItemChange = (itemId, checked) => {
    let newSelectedItems;

    if (checked) {
      newSelectedItems = [...selectedItems, itemId];
    } else {
      newSelectedItems = selectedItems.filter((id) => id !== itemId);
    }

    setSelectedItems(newSelectedItems);

    if (newSelectedItems.length === trailsLegends.length && trailsLegends.length > 0) {
      onChange && onChange("all");
    } else {
      onChange && onChange(newSelectedItems);
    }
  };

  const isItemSelected = (itemId) => {
    return selectedItems.includes(itemId);
  };

  const closeFilters = () => {
    setIsFiltersOpened(false)
  };

  const openFilters = (event) => {
    setIsFiltersOpened(event.currentTarget)
  }

  const open = Boolean(isFiltersOpened);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Fragment>
      <button
        onClick={openFilters}
        className={'legend-name filter-events-map-button'}
      >
        {messages["filterEventsMap_buttonLabel"]}
        <Icons name="filter_list" size="small" color={open ? "#00AEEF" : "inherit"} />
      </button>

      <Popover
        id={id}
        open={open}
        anchorEl={isFiltersOpened}
        onClose={closeFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <div className="filter-events-map-menu">
          <div className="filter-events-map-container">
            {loadVehicleTrails ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100, width: 200 }}>
                <Loading />
              </div>
            ) : (
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={0}
              >
                {trailsLegends?.map((item) => {

                  const isSelected = isItemSelected(item.id);

                  return (
                    <Grid item key={item.id} className="filter-events-map-item">
                      <Box
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "0px",
                          paddingLeft: "10px",
                        }}
                      >
                        <Checkbox
                          id={`filter-event-${item.id}`}
                          value={isSelected}
                          onChange={(checked) => handleItemChange(item.id, checked)}
                          size="sm"
                          style={{ marginRight: 0 }}
                        />

                        {
                          item.isControl ? (
                            <div key={item.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flex: 1,
                            }}>
                              <div className={'legend-iconFlag'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px' }}>
                                {item.control}
                              </div>
                              <span className={'legend-name'}>{item.name}</span>
                            </div>
                          ) : (
                            <div
                              key={item.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flex: 1,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px' }}>
                                {
                                  item?.icon ? (
                                    <img
                                      src={item?.icon}
                                      alt={item?.name}
                                      style={{ width: 18, height: 18 }}
                                    />
                                  ) : <NavigationIcon className={'navigation-icon'} style={{ color: `${item.color}`, fontSize: 18 }} />
                                }
                              </div>
                              <span className={'legend-name'}>{item.name}</span>
                            </div>
                          )
                        }
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </div>
        </div>
      </Popover>
    </Fragment>
  );
};

FilterEventsMap.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.node,
      ]),
    }),
  ),
  onChange: PropTypes.func,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  buttonLabel: PropTypes.string,
  buttonIcon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.node,
  ]),
};

export default FilterEventsMap;
